import { lazy, Suspense, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Archive, ArrowLeft, Bell, Bookmark, BookOpen, Building2, Camera, Check, ChevronRight,
  Clock3, Download, FileText, GraduationCap, Hash, Home, LogOut, Pencil, Plus, RefreshCw,
  ScanLine, Search, Server, Settings, ShieldCheck, Sparkles, Upload, User, Users, WifiOff
} from "lucide-react";
import { Link, Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { api, query } from "./api.js";
import {
  createCatalogEntry, exportRepository, photosToPDF, publishMaterial, publishMaterialVersion,
  publishProfile, setRating, toggleFollow, toggleSave, updateProfile
} from "./atproto.js";
import { useAuth } from "./auth.jsx";
import { Avatar, EmptyState, Loading, MaterialCard, Overlay, PageHeader, PliegoLogo, RatingPicker, Stars, formatBytes, labelForType } from "./components.jsx";
import {
  clearReadingHistory, isMaterialOffline, offlineMaterials, readingHistory,
  removeMaterialOffline, saveMaterialOffline
} from "./library.js";

const PDFViewer = lazy(() => import("./PDFViewer.jsx").then(module => ({ default: module.PDFViewer })));

export default function App() {
  const { profile, booting } = useAuth();
  if (import.meta.env.DEV) {
    const preview = new URLSearchParams(window.location.search).get("preview");
    if (preview === "feed" || preview === "app") return <PreviewApp />;
    if (preview === "pdf") return <PreviewPDF />;
    if (preview === "settings") return <SettingsPage />;
  }
  if (booting) return <div className="boot-screen"><PliegoLogo /><Loading label="Abriendo tu mesa…" /></div>;
  return <Routes>
    <Route path="/privacidad" element={<LegalPage kind="privacy" />} />
    <Route path="/condiciones" element={<LegalPage kind="terms" />} />
    <Route path="/contacto" element={<ContactPage />} />
    <Route path="*" element={!profile ? <AuthPage /> : !profile.onboardingCompleted ? <OnboardingPage /> : <AuthenticatedApp />} />
  </Routes>;
}

function AuthenticatedApp() {
  return <AppShell><Routes>
    <Route path="/" element={<FeedPage />} />
    <Route path="/explorar" element={<ExplorePage />} />
    <Route path="/subir" element={<UploadPage />} />
    <Route path="/guardados" element={<SavedPage />} />
    <Route path="/perfil" element={<OwnProfilePage />} />
    <Route path="/ajustes" element={<SettingsPage />} />
    <Route path="/perfil/:identifier" element={<ProfilePage />} />
    <Route path="/material/:id" element={<MaterialPage />} />
    <Route path="/etiqueta/:tag" element={<TagPage />} />
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes></AppShell>;
}

function AuthPage() {
  const { signIn, error } = useAuth();
  const [username, setUsername] = useState("");
  const [working, setWorking] = useState(false);
  const submit = async event => {
    event.preventDefault();
    const cleanUsername = username.trim().replace(/^@/, "").replace(/\.bsky\.social$/i, "");
    if (!cleanUsername) return;
    setWorking(true);
    try { await signIn(`${cleanUsername}.bsky.social`); }
    catch { setWorking(false); }
  };
  return <main className="auth-page">
    <header className="auth-topbar"><PliegoLogo /></header>
    <section className="auth-copy">
      <span className="eyebrow">Tu red para estudiar</span>
      <h1>Tus apuntes, en buena compañía.</h1>
      <p>Encuentra materiales útiles, sigue lo que estudias y comparte tus propios pliegos.</p>
      <div className="auth-proof"><span><Users /> Comunidad</span><span><ShieldCheck /> Archivos en tu PDS</span><span><Sparkles /> Sin almacén central</span></div>
    </section>
    <section className="auth-card">
      <span className="auth-card__icon"><User /></span>
      <h2>Entra en Pliego</h2>
      <p>Escribe tu usuario de Bluesky. La contraseña se introduce después, directamente en Bluesky.</p>
      <form onSubmit={submit}>
        <label>Tu usuario de Bluesky</label>
        <div className="input-with-prefix input-with-domain"><span className="input-at">@</span><input value={username} onChange={event => setUsername(event.target.value)} placeholder="tuusuario" autoCapitalize="none" autoCorrect="off" spellCheck="false" inputMode="text" /><span className="input-domain">.bsky.social</span></div>
        {error && <p className="form-error">{error}</p>}
        <button className="primary-button" disabled={working || !username.trim()}>{working ? "Abriendo Bluesky…" : "Continuar con Bluesky"}<ChevronRight /></button>
      </form>
      <p className="fine-print"><ShieldCheck /> Pliego nunca ve tu contraseña.</p>
      <a className="auth-create-link" href="https://bsky.app" target="_blank" rel="noreferrer">Crear una cuenta en Bluesky <ChevronRight /></a>
    </section>
  </main>;
}

function OnboardingPage() {
  const { agent, refreshProfile, logout } = useAuth();
  const [regions, setRegions] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [avatar, setAvatar] = useState(null);
  const [form, setForm] = useState({
    displayName: "", description: "", regionCode: "", programId: "", institutionId: "",
    courseLabel: "", subjectIds: []
  });
  useEffect(() => {
    Promise.all([query("/v1/catalog/regions"), query("/v1/catalog/programs")]).then(([regionData, programData]) => {
      setRegions(regionData.regions); setPrograms(programData.programs);
    }).catch(reason => setError(reason.message));
  }, []);
  useEffect(() => {
    if (!form.programId) return setSubjects([]);
    query("/v1/catalog/subjects", { programId: form.programId }).then(data => setSubjects(data.subjects)).catch(() => setSubjects([]));
  }, [form.programId]);
  const nextDisabled = step === 0 ? !form.displayName.trim() : step === 1 ? !form.regionCode || !form.programId : false;
  const finish = async () => {
    setSaving(true); setError("");
    try {
      await publishProfile(agent, form, avatar);
      await refreshProfile(agent.assertDid);
    } catch (reason) { setError(reason.message); } finally { setSaving(false); }
  };
  return <main className="onboarding-page">
    <header><PliegoLogo compact /><button className="text-button" onClick={logout}>Salir</button></header>
    <div className="onboarding-progress"><span style={{ width: `${(step + 1) / 3 * 100}%` }} /></div>
    <section className="onboarding-card">
      <span className="eyebrow">Paso {step + 1} de 3</span>
      {step === 0 && <>
        <h1>Primero, ponle nombre a tu mesa.</h1>
        <p>Esto es lo que verá la gente cuando compartas un material.</p>
        <div className="avatar-picker">
          <label>{avatar ? <img src={URL.createObjectURL(avatar)} alt="Vista previa" /> : <User />}<input type="file" accept="image/png,image/jpeg,image/webp" onChange={event => setAvatar(event.target.files?.[0] || null)} /></label>
          <span>Foto opcional · máx. 2 MB</span>
        </div>
        <Field label="Nombre visible"><input value={form.displayName} onChange={event => setForm({ ...form, displayName: event.target.value })} placeholder="Cómo quieres aparecer" /></Field>
        <Field label="Sobre ti" hint="Opcional"><textarea value={form.description} onChange={event => setForm({ ...form, description: event.target.value })} placeholder="Qué estudias, qué te interesa…" /></Field>
      </>}
      {step === 1 && <>
        <h1>¿Qué estás estudiando?</h1>
        <p>Lo usaremos para construir un feed que tenga sentido desde el primer día.</p>
        <Field label="Comunidad autónoma"><select value={form.regionCode} onChange={event => setForm({ ...form, regionCode: event.target.value })}><option value="">Elige una</option>{regions.map(item => <option key={item.code} value={item.code}>{item.name}</option>)}</select></Field>
        <Field label="Curso, FP o carrera"><select value={form.programId} onChange={event => setForm({ ...form, programId: event.target.value, subjectIds: [] })}><option value="">Elige qué estudias</option>{programs.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select></Field>
        <Field label="Curso actual" hint="Opcional"><input value={form.courseLabel} onChange={event => setForm({ ...form, courseLabel: event.target.value })} placeholder="1º, 2º, tercer semestre…" /></Field>
      </>}
      {step === 2 && <>
        <h1>Elige tus asignaturas.</h1>
        <p>Sigue sólo lo que te interesa. Podrás cambiarlo después.</p>
        <div className="subject-grid">{subjects.length ? subjects.map(subject => {
          const selected = form.subjectIds.includes(subject.id);
          return <button type="button" key={subject.id} className={selected ? "subject-option selected" : "subject-option"} onClick={() => setForm({ ...form, subjectIds: selected ? form.subjectIds.filter(id => id !== subject.id) : [...form.subjectIds, subject.id] })}><span>{subject.name}</span>{selected && <Check />}</button>;
        }) : <p className="muted">Este programa aún no tiene asignaturas verificadas. Podrás seguirlas cuando la comunidad las añada.</p>}</div>
      </>}
      {error && <p className="form-error">{error}</p>}
      <footer>
        {step > 0 && <button className="secondary-button" onClick={() => setStep(step - 1)}>Atrás</button>}
        {step < 2
          ? <button className="primary-button" disabled={nextDisabled} onClick={() => setStep(step + 1)}>Continuar<ChevronRight /></button>
          : <button className="primary-button" disabled={saving} onClick={finish}>{saving ? "Publicando tu perfil…" : "Entrar en Pliego"}<Sparkles /></button>}
      </footer>
    </section>
  </main>;
}

function AppShell({ children }) {
  const { profile, logout } = useAuth();
  const location = useLocation();
  const [showNotifications, setShowNotifications] = useState(false);
  useLayoutEffect(() => {
    // BrowserRouter preserves the document scroll between routes. On mobile,
    // opening a shorter page from the bottom of a long feed can otherwise leave
    // the viewport below the new content, making the app look completely blank.
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [location.pathname]);
  const navigation = [
    ["/", "Inicio", Home], ["/explorar", "Explorar", Search], ["/subir", "Subir", Upload],
    ["/guardados", "Guardados", Bookmark], ["/perfil", "Perfil", User]
  ];
  return <div className="app-shell">
    <aside className="sidebar">
      <PliegoLogo />
      <nav>{navigation.map(([to, label, Icon]) => <NavItem key={to} to={to} label={label} icon={Icon} active={to === "/" ? location.pathname === "/" : location.pathname.startsWith(to)} />)}</nav>
      <div className="sidebar__account"><Avatar profile={profile} /><span><strong>{profile.displayName}</strong><small>@{profile.handle}</small></span><Link to="/ajustes" aria-label="Ajustes"><Settings /></Link><button onClick={logout} aria-label="Cerrar sesión"><LogOut /></button></div>
    </aside>
    <div className="app-main">
      <div className="utility-bar"><span className="utility-bar__course">{profile.studies?.[0]?.program.name || "Tu mesa"}</span><button className="icon-button notification-button" onClick={() => setShowNotifications(true)} aria-label="Notificaciones"><Bell /></button></div>
      {children}
    </div>
    <nav className="mobile-tabs">{navigation.map(([to, label, Icon]) => <NavItem key={to} to={to} label={label} icon={Icon} active={to === "/" ? location.pathname === "/" : location.pathname.startsWith(to)} />)}</nav>
    {showNotifications && <NotificationsOverlay onClose={() => setShowNotifications(false)} />}
  </div>;
}

function NavItem({ to, label, icon: Icon, active }) {
  return <Link to={to} className={active ? "nav-item active" : "nav-item"}><Icon /><span>{label}</span></Link>;
}

function FeedPage() {
  const { profile, agent } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const load = () => query("/v1/feed", { viewerDid: profile.did }).then(setData).catch(reason => setError(reason.message));
  useEffect(() => { load(); }, [profile.did]);
  return <main className="page feed-page">
    <PageHeader eyebrow="Tu red educativa" title="lo nuevo en tu mesa"><p>Apuntes de personas, asignaturas y cursos que sigues.</p></PageHeader>
    {error && <ErrorNotice message={error} retry={load} />}
    {!data ? <Loading label="Buscando pliegos nuevos…" /> : data.materials.length
      ? <div className="material-list">{data.materials.map(material => <MaterialCard key={material.id} material={material} onToggleSave={(item, saved) => toggleSave(agent, item, saved)} />)}</div>
      : <EmptyState icon={BookOpen} title="Tu mesa está lista" body="Sigue personas, cursos, asignaturas o etiquetas para recibir aquí sus próximos materiales." action={<Link className="primary-button compact" to="/explorar">Explorar comunidad</Link>} />}
  </main>;
}

function ExplorePage() {
  const { profile, agent } = useAuth();
  const navigate = useNavigate();
  const [term, setTerm] = useState("");
  const [results, setResults] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [followed, setFollowed] = useState({ programs: [], subjects: [], tags: [] });
  const [loading, setLoading] = useState(false);
  const [showCreator, setShowCreator] = useState(false);
  const [catalogRevision, setCatalogRevision] = useState(0);
  useEffect(() => {
    Promise.all([
      query("/v1/catalog/programs", { viewerDid: profile.did }),
      query("/v1/catalog/subjects", { viewerDid: profile.did }),
      query("/v1/following/catalog", { viewerDid: profile.did })
    ]).then(([programData, subjectData, followedData]) => {
      setPrograms(programData.programs);
      setSubjects(subjectData.subjects);
      setFollowed({ ...followedData, tags: followedData.tags || [] });
    });
  }, [profile.did, catalogRevision]);
  useEffect(() => {
    if (term.trim().length < 2) return setResults(null);
    const timer = setTimeout(() => {
      setLoading(true);
      query("/v1/search", { q: term.trim(), viewerDid: profile.did }).then(setResults).finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(timer);
  }, [term, profile.did]);
  const follow = async (type, item) => {
    const next = !item.viewer?.following;
    if (type === "program") setPrograms(values => values.map(value => value.id === item.id ? { ...value, viewer: { following: next } } : value));
    if (type === "subject") setSubjects(values => values.map(value => value.id === item.id ? { ...value, viewer: { following: next } } : value));
    await toggleFollow(agent, type, item.id, next);
  };
  const followedItems = [...followed.programs, ...followed.subjects, ...(followed.tags || [])];
  return <main className="page explore-page">
    <PageHeader
      eyebrow="Toda la comunidad"
      title="Explorar"
      action={<button className="secondary-button compact" onClick={() => setShowCreator(true)}><Plus />Añadir a la comunidad</button>}
    />
    <div className="search-box"><Search /><input value={term} onChange={event => setTerm(event.target.value)} placeholder="Título, asignatura, curso, centro o persona" /></div>
    <section className="create-institution-card">
      <span className="create-institution-card__icon"><Building2 /></span>
      <div><strong>¿No encuentras tu centro?</strong><p>Cualquier estudiante puede añadir un instituto, colegio, centro de FP o universidad.</p></div>
      <button className="primary-button compact" onClick={() => setShowCreator(true)}><Plus />Crear centro</button>
    </section>
    {loading && <Loading label="Buscando…" />}
    {results ? <SearchResults results={results} agent={agent} onCreateInstitution={() => setShowCreator(true)} /> : <>
      {!!followedItems.length && <>
        <SectionHeader title="Lo que sigues" detail="Tus muros" />
        <div className="entity-grid">
          {followed.programs.map(program => <EntityCard key={program.id} icon={GraduationCap} title={program.name} subtitle={program.levelLabel} following />)}
          {followed.subjects.map(subject => <EntityCard key={subject.id} icon={BookOpen} title={subject.name} subtitle={subject.courseLabel || subject.programName} following />)}
          {(followed.tags || []).map(tag => <EntityCard key={tag.name} icon={Hash} title={`#${tag.name}`} subtitle={`${tag.counts?.materials || 0} materiales`} following onOpen={() => navigate(`/etiqueta/${encodeURIComponent(tag.name)}`)} />)}
        </div>
      </>}
      <SectionHeader title="Cursos y titulaciones" detail="Síguelos como un muro" />
      <div className="entity-grid">{programs.map(program => <EntityCard key={program.id} icon={GraduationCap} title={program.name} subtitle={`${program.levelLabel} · ${program.counts?.materials || 0} materiales`} following={program.viewer?.following} onFollow={() => follow("program", program)} />)}</div>
      <SectionHeader title="Asignaturas" detail="Lo que estás cursando ahora" />
      <div className="entity-grid">{subjects.map(subject => <EntityCard key={subject.id} icon={BookOpen} title={subject.name} subtitle={`${subject.courseLabel || subject.programName} · ${subject.counts?.materials || 0} materiales`} following={subject.viewer?.following} onFollow={() => follow("subject", subject)} />)}</div>
    </>}
    {showCreator && <CommunityCatalogOverlay
      agent={agent}
      viewer={profile}
      onClose={() => setShowCreator(false)}
      onCreated={() => { setShowCreator(false); setCatalogRevision(value => value + 1); }}
    />}
  </main>;
}

function SearchResults({ results, agent, onCreateInstitution }) {
  const navigate = useNavigate();
  const [people, setPeople] = useState(results.people);
  const [programs, setPrograms] = useState(results.programs);
  const [subjects, setSubjects] = useState(results.subjects);
  const [tags, setTags] = useState(results.tags || []);
  useEffect(() => {
    setPeople(results.people); setPrograms(results.programs); setSubjects(results.subjects); setTags(results.tags || []);
  }, [results]);
  const follow = async (type, item) => {
    const key = type === "tag" ? "name" : "id";
    const next = !item.viewer?.following;
    const update = values => values.map(value => value[key] === item[key] ? { ...value, viewer: { following: next } } : value);
    if (type === "program") setPrograms(update);
    if (type === "subject") setSubjects(update);
    if (type === "tag") setTags(update);
    try { await toggleFollow(agent, type, item[key], next); }
    catch {
      const rollback = values => values.map(value => value[key] === item[key] ? item : value);
      if (type === "program") setPrograms(rollback);
      if (type === "subject") setSubjects(rollback);
      if (type === "tag") setTags(rollback);
    }
  };
  const hasAny = results.materials.length || people.length || programs.length || subjects.length || results.institutions.length || tags.length;
  if (!hasAny) return <EmptyState icon={Search} title="No aparece nada todavía" body="Prueba con menos palabras o añade el centro si aún no existe." action={<button className="primary-button compact" onClick={onCreateInstitution}><Building2 />Crear centro</button>} />;
  return <div className="search-results">
    {!!results.materials.length && <><SectionHeader title="Materiales" /><div className="material-list">{results.materials.map(item => <MaterialCard key={item.id} material={item} onToggleSave={(material, saved) => toggleSave(agent, material, saved)} />)}</div></>}
    {!!people.length && <><SectionHeader title="Personas" /><div className="people-grid">{people.map(person => <button className="person-card" key={person.did} onClick={() => navigate(`/perfil/${encodeURIComponent(person.did)}`)}><Avatar profile={person} /><span><strong>{person.displayName}</strong><small>@{person.handle}</small></span><ChevronRight /></button>)}</div></>}
    {!!programs.length && <><SectionHeader title="Cursos y titulaciones" /><div className="entity-grid">{programs.map(item => <EntityCard key={item.id} icon={GraduationCap} title={item.name} subtitle={item.levelLabel} following={item.viewer?.following} onFollow={() => follow("program", item)} />)}</div></>}
    {!!subjects.length && <><SectionHeader title="Asignaturas" /><div className="entity-grid">{subjects.map(item => <EntityCard key={item.id} icon={BookOpen} title={item.name} subtitle={item.courseLabel || item.programName} following={item.viewer?.following} onFollow={() => follow("subject", item)} />)}</div></>}
    {!!tags.length && <><SectionHeader title="Etiquetas" /><div className="entity-grid">{tags.map(item => <EntityCard key={item.name} icon={Hash} title={`#${item.name}`} subtitle={`${item.counts?.materials || 0} materiales`} following={item.viewer?.following} onFollow={() => follow("tag", item)} onOpen={() => navigate(`/etiqueta/${encodeURIComponent(item.name)}`)} />)}</div></>}
    {!!results.institutions.length && <><SectionHeader title="Centros" /><div className="entity-grid">{results.institutions.map(item => <EntityCard key={item.id} icon={Building2} title={item.name} subtitle={item.city || item.regionName} />)}</div></>}
  </div>;
}

function UploadPage() {
  const { profile, agent } = useAuth();
  const navigate = useNavigate();
  const fileInput = useRef();
  const cameraInput = useRef();
  const study = profile.studies?.[0];
  const [file, setFile] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [fields, setFields] = useState({
    title: "", description: "", documentType: "apuntes", programId: study?.program.id || "",
    subjectId: "", institutionId: study?.institution?.id || "", courseLabel: study?.courseLabel || "",
    academicYear: new Date().getFullYear().toString(), tags: "", rightsBasis: "own", licenseName: "", licenseURL: ""
  });
  useEffect(() => {
    if (fields.programId) query("/v1/catalog/subjects", { programId: fields.programId }).then(data => setSubjects(data.subjects));
  }, [fields.programId]);
  const submit = async event => {
    event.preventDefault(); setError("");
    try {
      const result = await publishMaterial(agent, file, fields, setStatus);
      navigate(`/material/${result.materialId}`);
    } catch (reason) { setError(reason.message); setStatus(""); }
  };
  const scanPhotos = async event => {
    const photos = event.target.files;
    if (!photos?.length) return;
    setError("");
    setStatus("Creando el PDF del escaneo…");
    try {
      const scanned = await photosToPDF(photos);
      setFile(scanned);
      setFields(current => ({ ...current, title: current.title || "Apuntes escaneados" }));
    } catch (reason) {
      setError(reason.message || "No se ha podido preparar el escaneo.");
    } finally { setStatus(""); }
  };
  return <main className="page upload-page">
    <PageHeader eyebrow="Publica desde tu PDS" title="subir un pliego"><p>El archivo se guarda con tu cuenta federada. Pliego sólo indexa sus etiquetas.</p></PageHeader>
    <form className="upload-layout" onSubmit={submit}>
      <div className={file ? "drop-zone selected" : "drop-zone"}>
        <input ref={fileInput} type="file" accept="application/pdf,.pdf" onChange={event => setFile(event.target.files?.[0] || null)} />
        <input ref={cameraInput} type="file" accept="image/jpeg,image/png" capture="environment" multiple onChange={scanPhotos} />
        <span className="drop-zone__icon">{file ? <Check /> : <Upload />}</span>
        <strong>{file ? file.name : "Añade tu material"}</strong>
        <small>{file ? formatBytes(file.size) : "PDF o escaneo de papel · máximo 100 MB"}</small>
        <div className="upload-source-actions">
          <button type="button" className="secondary-button compact" onClick={() => fileInput.current?.click()}><FileText />Elegir PDF</button>
          <button type="button" className="secondary-button compact" onClick={() => cameraInput.current?.click()}><ScanLine />Escanear</button>
        </div>
      </div>
      <div className="form-sheet">
        <Field label="Título"><input required value={fields.title} onChange={event => setFields({ ...fields, title: event.target.value })} placeholder="Qué contiene este material" /></Field>
        <div className="field-row"><Field label="Tipo"><select value={fields.documentType} onChange={event => setFields({ ...fields, documentType: event.target.value })}><option value="apuntes">Apuntes</option><option value="resumen">Resumen</option><option value="ejercicios">Ejercicios</option><option value="examen">Examen</option><option value="libro_autorizado">Libro autorizado</option><option value="otro">Otro</option></select></Field><Field label="Asignatura"><select value={fields.subjectId} onChange={event => setFields({ ...fields, subjectId: event.target.value })}><option value="">General del curso</option>{subjects.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select></Field></div>
        <Field label="Descripción" hint="Opcional"><textarea value={fields.description} onChange={event => setFields({ ...fields, description: event.target.value })} placeholder="Tema, unidades, para qué sirve…" /></Field>
        <Field label="Etiquetas" hint="Separadas por comas"><input value={fields.tags} onChange={event => setFields({ ...fields, tags: event.target.value })} placeholder="selectividad, tema 4, resuelto" /></Field>
        <Field label="Derechos del archivo"><select value={fields.rightsBasis} onChange={event => setFields({ ...fields, rightsBasis: event.target.value })}><option value="own">Es mío y puedo compartirlo</option><option value="permission">Tengo permiso</option><option value="open_license">Tiene licencia abierta</option><option value="public_domain">Es de dominio público</option></select></Field>
        {fields.rightsBasis === "open_license" && <div className="field-row"><Field label="Nombre de licencia"><input value={fields.licenseName} onChange={event => setFields({ ...fields, licenseName: event.target.value })} placeholder="CC BY 4.0" /></Field><Field label="Enlace de licencia"><input value={fields.licenseURL} onChange={event => setFields({ ...fields, licenseURL: event.target.value })} placeholder="https://…" /></Field></div>}
        <div className="rights-note"><ShieldCheck /><p><strong>Publica sólo contenido que puedas compartir.</strong><br />No subas libros comerciales ni trabajos ajenos sin autorización.</p></div>
        {error && <p className="form-error">{error}</p>}
        <button className="primary-button" disabled={!file || !fields.title.trim() || Boolean(status)}>{status || "Publicar material"}<Upload /></button>
      </div>
    </form>
  </main>;
}

function SavedPage() {
  const { profile, agent } = useAuth();
  const [materials, setMaterials] = useState(null);
  const [libraryRevision, setLibraryRevision] = useState(0);
  const load = () => query("/v1/saved", { viewerDid: profile.did }).then(data => setMaterials(data.materials));
  useEffect(() => { load(); }, [profile.did]);
  useEffect(() => {
    const refresh = () => setLibraryRevision(value => value + 1);
    window.addEventListener("pliego-library-change", refresh);
    return () => window.removeEventListener("pliego-library-change", refresh);
  }, []);
  const history = useMemo(() => readingHistory(), [libraryRevision]);
  const offline = useMemo(() => offlineMaterials(), [libraryRevision]);
  return <main className="page">
    <PageHeader eyebrow="Tu biblioteca" title="Guardados"><p>PDF que quieres tener a mano.</p></PageHeader>
    {!!history.length && <section className="library-section">
      <SectionHeader title="Continuar leyendo" detail={<button className="text-button" onClick={clearReadingHistory}>Borrar historial</button>} />
      <div className="continue-grid">{history.slice(0, 8).map(entry => <Link key={entry.materialID} className="continue-card" to={`/material/${encodeURIComponent(entry.materialID)}`}><Clock3 /><span><strong>{entry.title}</strong><small>Página {entry.page} de {entry.totalPages || "—"}</small></span><ChevronRight /></Link>)}</div>
    </section>}
    {!!offline.length && <section className="library-section">
      <SectionHeader title="Sin conexión" detail={`${offline.length} documentos`} />
      <div className="continue-grid">{offline.map(item => <Link key={item.id} className="continue-card" to={`/material/${encodeURIComponent(item.id)}`}><WifiOff /><span><strong>{item.title}</strong><small>Disponible sin internet</small></span><ChevronRight /></Link>)}</div>
    </section>}
    {!materials ? <Loading /> : materials.length ? <div className="material-list">{materials.map(material => <MaterialCard key={material.id} material={material} showDownloadLabel onToggleSave={async (item, saved) => { await toggleSave(agent, item, saved); load(); }} />)}</div> : <EmptyState icon={Bookmark} title="Aún no has guardado nada" body="Toca el marcador de cualquier material para verlo aquí." />}
  </main>;
}

function OwnProfilePage() {
  const { profile } = useAuth();
  return <ProfileView identifier={profile.did} own />;
}

function ProfilePage() {
  const { identifier } = useParams();
  return <ProfileView identifier={identifier} />;
}

function ProfileView({ identifier, own = false }) {
  const { profile: viewer, agent, refreshProfile } = useAuth();
  const [person, setPerson] = useState(own ? viewer : null);
  const [materials, setMaterials] = useState(null);
  const [relationship, setRelationship] = useState(null);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState("");
  const load = () => Promise.all([
    query(`/v1/profiles/${encodeURIComponent(identifier)}`, { viewerDid: viewer.did }),
    query(`/v1/profiles/${encodeURIComponent(identifier)}/materials`, { viewerDid: viewer.did })
  ]).then(([profileData, materialData]) => { setPerson(profileData.profile); setMaterials(materialData.materials); }).catch(reason => setError(reason.message));
  useEffect(() => { load(); }, [identifier, viewer.did]);
  if (error) return <main className="page"><ErrorNotice message={error} /></main>;
  if (!person || !materials) return <main className="page"><Loading label="Abriendo perfil…" /></main>;
  const study = person.studies?.[0];
  const follow = async () => {
    const next = !person.viewer.following;
    setPerson({ ...person, viewer: { following: next }, counts: { ...person.counts, followers: person.counts.followers + (next ? 1 : -1) } });
    try { await toggleFollow(agent, "user", person.did, next); } catch { load(); }
  };
  return <main className="page profile-page">
    <section className="profile-hero">
      <div className="profile-hero__identity"><Avatar profile={person} size="large" /><div><span className="eyebrow">@{person.handle}</span><h1>{person.displayName}</h1><p>{person.description || "Sin biografía todavía."}</p></div></div>
      <div className="profile-hero__study"><GraduationCap /><span><small>{person.regionName || "Comunidad educativa"}</small><strong>{study?.program.name || "Explorando Pliego"}</strong><small>{study?.institution?.name || study?.courseLabel}</small></span></div>
      <div className="profile-stats"><button onClick={() => setRelationship("followers")}><strong>{person.counts.followers}</strong><span>Seguidores</span></button><button onClick={() => setRelationship("following")}><strong>{person.counts.following}</strong><span>Siguiendo</span></button><div><strong>{person.counts.materials}</strong><span>Materiales</span></div></div>
      {own && <div className="profile-own-actions"><button className="secondary-button profile-edit-button" onClick={() => setEditing(true)}><Pencil />Editar perfil</button><Link className="icon-button" to="/ajustes" aria-label="Ajustes"><Settings /></Link></div>}
      {!own && person.did !== viewer.did && <button className={person.viewer.following ? "secondary-button" : "primary-button"} onClick={follow}>{person.viewer.following ? "Siguiendo" : "Seguir"}</button>}
    </section>
    {!!person.subjects?.length && <div className="profile-subjects">{person.subjects.map(subject => <span key={subject.id}>{subject.name}</span>)}</div>}
    <SectionHeader title="Materiales publicados" detail={`${materials.length} pliegos`} />
    {materials.length ? <div className="material-list">{materials.map(material => <MaterialCard key={material.id} material={material} onToggleSave={(item, saved) => toggleSave(agent, item, saved)} />)}</div> : <EmptyState icon={FileText} title="Aún no hay materiales" body={own ? "Cuando publiques tu primer PDF aparecerá aquí." : "Esta persona todavía no ha publicado ningún PDF."} />}
    {relationship && <RelationshipsOverlay profile={person} kind={relationship} viewerDid={viewer.did} onClose={() => setRelationship(null)} />}
    {editing && <EditProfileOverlay
      profile={person}
      agent={agent}
      onClose={() => setEditing(false)}
      onSaved={async () => {
        const fresh = await refreshProfile(viewer.did);
        if (fresh) setPerson(fresh);
        setEditing(false);
        load();
      }}
    />}
  </main>;
}

function EditProfileOverlay({ profile, agent, onClose, onSaved }) {
  const study = profile.studies?.[0];
  const [regions, setRegions] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [avatar, setAvatar] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [showInstitutionCreator, setShowInstitutionCreator] = useState(false);
  const [institutionRevision, setInstitutionRevision] = useState(0);
  const [form, setForm] = useState({
    displayName: profile.displayName,
    description: profile.description || "",
    regionCode: profile.regionCode || "",
    kind: study?.program.kind || "universidad",
    programId: study?.program.id || "",
    institutionId: study?.institution?.id || "",
    courseLabel: study?.courseLabel || "",
    subjectIds: profile.subjects?.map(subject => subject.id) || []
  });
  const avatarPreview = useMemo(() => avatar ? URL.createObjectURL(avatar) : profile.avatarURL, [avatar, profile.avatarURL]);

  useEffect(() => () => { if (avatar) URL.revokeObjectURL(avatarPreview); }, [avatar, avatarPreview]);
  useEffect(() => {
    Promise.all([query("/v1/catalog/regions"), query("/v1/catalog/programs")])
      .then(([regionData, programData]) => {
        setRegions(regionData.regions);
        setPrograms(programData.programs);
      })
      .catch(reason => setError(reason.message));
  }, []);
  useEffect(() => {
    if (!form.regionCode) return setInstitutions([]);
    query("/v1/catalog/institutions", { region: form.regionCode })
      .then(data => setInstitutions(data.institutions))
      .catch(() => setInstitutions([]));
  }, [form.regionCode, institutionRevision]);
  useEffect(() => {
    if (!form.programId) return setSubjects([]);
    query("/v1/catalog/subjects", { programId: form.programId })
      .then(data => setSubjects(data.subjects))
      .catch(() => setSubjects([]));
  }, [form.programId]);

  const submit = async event => {
    event.preventDefault();
    const program = programs.find(item => item.id === form.programId) || study?.program;
    if (!program || !form.displayName.trim() || !form.regionCode) return;
    setSaving(true);
    setError("");
    try {
      await updateProfile(agent, form, avatar, program);
      await onSaved();
    } catch (reason) {
      setError(reason.message || "No se ha podido guardar el perfil.");
      setSaving(false);
    }
  };
  const filteredPrograms = programs.filter(program => program.kind === form.kind);

  return <><Overlay title="Editar perfil" onClose={onClose} wide>
    <form className="profile-editor" onSubmit={submit}>
      <div className="profile-editor__avatar">
        <label>
          {avatarPreview ? <img src={avatarPreview} alt="Vista previa de tu foto" /> : <User />}
          <span><Camera />Cambiar foto</span>
          <input type="file" accept="image/png,image/jpeg,image/webp" onChange={event => setAvatar(event.target.files?.[0] || null)} />
        </label>
        <small>JPG, PNG o WebP · máximo 2 MB</small>
      </div>

      <div className="field-row">
        <Field label="Nombre visible"><input required value={form.displayName} onChange={event => setForm({ ...form, displayName: event.target.value })} /></Field>
        <Field label="Comunidad autónoma"><select required value={form.regionCode} onChange={event => setForm({ ...form, regionCode: event.target.value, institutionId: "" })}><option value="">Elige una</option>{regions.map(region => <option key={region.code} value={region.code}>{region.name}</option>)}</select></Field>
      </div>
      <Field label="Sobre ti" hint="Opcional"><textarea value={form.description} onChange={event => setForm({ ...form, description: event.target.value })} /></Field>

      <SectionHeader title="Qué estás cursando" />
      <div className="field-row">
        <Field label="Tipo de estudios"><select value={form.kind} onChange={event => setForm({ ...form, kind: event.target.value, programId: "", institutionId: "", subjectIds: [] })}><option value="bachillerato">Bachillerato</option><option value="fp">Formación Profesional</option><option value="universidad">Universidad</option></select></Field>
        <Field label="Grado, carrera o ciclo"><select required value={form.programId} onChange={event => setForm({ ...form, programId: event.target.value, subjectIds: [] })}><option value="">Elige uno</option>{filteredPrograms.map(program => <option key={program.id} value={program.id}>{program.name}</option>)}</select></Field>
      </div>
      <div className="field-row">
        <Field label="Centro" hint="Opcional"><select value={form.institutionId} onChange={event => setForm({ ...form, institutionId: event.target.value })}><option value="">Sin centro concreto</option>{institutions.map(institution => <option key={institution.id} value={institution.id}>{institution.name}</option>)}</select></Field>
        <Field label="Curso actual" hint="Opcional"><input value={form.courseLabel} onChange={event => setForm({ ...form, courseLabel: event.target.value })} placeholder="1º, 2º, tercer semestre…" /></Field>
      </div>
      <button type="button" className="inline-community-action" onClick={() => setShowInstitutionCreator(true)}><Building2 />¿No aparece tu centro? Añádelo</button>

      {!!form.programId && <div className="profile-editor__subjects">
        <span className="field-label">Asignaturas</span>
        {subjects.length ? <div className="subject-grid">{subjects.map(subject => {
          const selected = form.subjectIds.includes(subject.id);
          return <button type="button" key={subject.id} className={selected ? "subject-option selected" : "subject-option"} onClick={() => setForm({ ...form, subjectIds: selected ? form.subjectIds.filter(id => id !== subject.id) : [...form.subjectIds, subject.id] })}><span>{subject.name}</span>{selected && <Check />}</button>;
        })}</div> : <p className="muted">Todavía no hay asignaturas para estos estudios.</p>}
      </div>}

      {error && <p className="form-error">{error}</p>}
      <button className="primary-button profile-editor__save" disabled={saving || !form.displayName.trim() || !form.regionCode || !form.programId}>{saving ? "Guardando…" : "Guardar cambios"}<Check /></button>
    </form>
  </Overlay>
  {showInstitutionCreator && <CommunityCatalogOverlay
    agent={agent}
    viewer={profile}
    onClose={() => setShowInstitutionCreator(false)}
    onCreated={() => {
      setShowInstitutionCreator(false);
      setInstitutionRevision(value => value + 1);
    }}
  />}</>;
}

function CommunityCatalogOverlay({ agent, viewer, onClose, onCreated }) {
  const [programs, setPrograms] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [regions, setRegions] = useState([]);
  const [courses, setCourses] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fields, setFields] = useState({
    entityType: "institution", name: "", regionCode: viewer.regionCode || "",
    institutionKind: "school", province: "", city: "", programKind: "bachillerato",
    levelLabel: "", institutionId: "", programId: "", courseId: "", courseLabel: "",
    semester: "", sortOrder: ""
  });

  useEffect(() => {
    Promise.all([
      query("/v1/catalog/regions"),
      query("/v1/catalog/programs"),
      query("/v1/catalog/institutions")
    ]).then(([regionData, programData, institutionData]) => {
      setRegions(regionData.regions);
      setPrograms(programData.programs);
      setInstitutions(institutionData.institutions);
    }).catch(reason => setError(reason.message));
  }, []);
  useEffect(() => {
    if (!fields.programId) return setCourses([]);
    query("/v1/catalog/courses", { programId: fields.programId })
      .then(data => setCourses(data.courses))
      .catch(() => setCourses([]));
  }, [fields.programId]);

  const valid = fields.name.trim() && (fields.entityType === "institution"
    ? fields.regionCode
    : fields.entityType === "program"
      ? fields.programKind
      : fields.programId);
  const submit = async event => {
    event.preventDefault();
    if (!valid) return;
    setSaving(true);
    setError("");
    try {
      await createCatalogEntry(agent, fields);
      onCreated();
    } catch (reason) {
      setError(reason.message || "No se ha podido añadir.");
      setSaving(false);
    }
  };

  return <Overlay title="Añadir a la comunidad" onClose={onClose} wide>
    <form className="catalog-editor" onSubmit={submit}>
      <p className="muted">Crea un centro, una titulación, un curso o una asignatura que falte. Se marcará como aportación comunitaria.</p>
      <Field label="Qué quieres añadir"><select value={fields.entityType} onChange={event => setFields({ ...fields, entityType: event.target.value })}><option value="institution">Centro de estudios</option><option value="program">Grado, carrera o ciclo</option><option value="course">Curso</option><option value="subject">Asignatura</option></select></Field>
      <Field label="Nombre"><input required value={fields.name} onChange={event => setFields({ ...fields, name: event.target.value })} placeholder="Nombre oficial o reconocible" /></Field>

      {fields.entityType === "institution" && <>
        <div className="field-row">
          <Field label="Tipo de centro"><select value={fields.institutionKind} onChange={event => setFields({ ...fields, institutionKind: event.target.value })}><option value="school">Instituto o colegio</option><option value="university">Universidad</option><option value="other">Otro centro</option></select></Field>
          <Field label="Comunidad autónoma"><select required value={fields.regionCode} onChange={event => setFields({ ...fields, regionCode: event.target.value })}><option value="">Elige una</option>{regions.map(region => <option key={region.code} value={region.code}>{region.name}</option>)}</select></Field>
        </div>
        <div className="field-row"><Field label="Provincia" hint="Opcional"><input value={fields.province} onChange={event => setFields({ ...fields, province: event.target.value })} /></Field><Field label="Localidad" hint="Opcional"><input value={fields.city} onChange={event => setFields({ ...fields, city: event.target.value })} /></Field></div>
      </>}

      {fields.entityType === "program" && <>
        <div className="field-row">
          <Field label="Tipo de estudios"><select value={fields.programKind} onChange={event => setFields({ ...fields, programKind: event.target.value })}><option value="bachillerato">Bachillerato</option><option value="fp">Formación Profesional</option><option value="universidad">Universidad</option></select></Field>
          <Field label="Centro" hint="Opcional"><select value={fields.institutionId} onChange={event => setFields({ ...fields, institutionId: event.target.value })}><option value="">Sin centro concreto</option>{institutions.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select></Field>
        </div>
        <Field label="Nivel" hint="Opcional"><input value={fields.levelLabel} onChange={event => setFields({ ...fields, levelLabel: event.target.value })} placeholder="Grado Superior, Grado universitario…" /></Field>
      </>}

      {(fields.entityType === "course" || fields.entityType === "subject") && <Field label="Titulación"><select required value={fields.programId} onChange={event => setFields({ ...fields, programId: event.target.value, courseId: "" })}><option value="">Elige una</option>{programs.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select></Field>}
      {fields.entityType === "course" && <Field label="Orden" hint="Opcional"><input type="number" min="0" max="99" value={fields.sortOrder} onChange={event => setFields({ ...fields, sortOrder: event.target.value })} placeholder="1, 2, 3…" /></Field>}
      {fields.entityType === "subject" && <>
        <Field label="Curso" hint="Opcional"><select value={fields.courseId} onChange={event => setFields({ ...fields, courseId: event.target.value })}><option value="">Sin curso concreto</option>{courses.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select></Field>
        {!fields.courseId && <Field label="Nombre del curso" hint="Opcional"><input value={fields.courseLabel} onChange={event => setFields({ ...fields, courseLabel: event.target.value })} placeholder="1º, segundo curso…" /></Field>}
        <Field label="Semestre" hint="Opcional"><input value={fields.semester} onChange={event => setFields({ ...fields, semester: event.target.value })} /></Field>
      </>}

      {error && <p className="form-error">{error}</p>}
      <button className="primary-button catalog-editor__save" disabled={saving || !valid}>{saving ? "Añadiendo…" : "Añadir a Pliego"}<Plus /></button>
    </form>
  </Overlay>;
}

function MaterialPage() {
  const { id } = useParams();
  const { profile, agent } = useAuth();
  const navigate = useNavigate();
  const [material, setMaterial] = useState(null);
  const [rating, setLocalRating] = useState(0);
  const [savingRating, setSavingRating] = useState(false);
  const [error, setError] = useState("");
  const [versions, setVersions] = useState([]);
  const [offline, setOffline] = useState(false);
  const [offlineWorking, setOfflineWorking] = useState(false);
  const [versionStatus, setVersionStatus] = useState("");
  const versionInput = useRef();
  const ratingRequest = useRef(false);
  const load = () => Promise.all([
    query(`/v1/materials/${encodeURIComponent(id)}`, { viewerDid: profile.did }),
    query(`/v1/materials/${encodeURIComponent(id)}/versions`, { viewerDid: profile.did }).catch(() => ({ materials: [] }))
  ]).then(([data, versionData]) => {
    setMaterial(data.material);
    setVersions(versionData.materials || []);
    setLocalRating(data.material.viewer.rating || 0);
    setOffline(isMaterialOffline(data.material.id));
    setError("");
  }).catch(reason => {
    const cached = offlineMaterials().find(item => item.id === id);
    if (cached) {
      setMaterial(cached);
      setVersions([cached]);
      setLocalRating(cached.viewer?.rating || 0);
      setOffline(true);
      setError("");
    } else setError(reason.message);
  });
  useEffect(() => { load(); }, [id, profile.did]);
  if (error && !material) return <main className="page"><button className="back-button" onClick={() => navigate(-1)}><ArrowLeft />Volver</button><ErrorNotice message={error} retry={load} /></main>;
  if (!material) return <main className="page"><Loading label="Abriendo material…" /></main>;
  const rate = async value => {
    if (ratingRequest.current) return;
    ratingRequest.current = true;
    const previous = rating;
    setLocalRating(value); setSavingRating(true);
    try { await setRating(agent, material, value); await load(); }
    catch (reason) { setLocalRating(previous); setError(reason.message); }
    finally { ratingRequest.current = false; setSavingRating(false); }
  };
  const downloadURL = `${material.fileURL}${material.fileURL.includes("?") ? "&" : "?"}download=1`;
  const followEntity = async (type, entity) => {
    const next = !entity.viewer?.following;
    const replaceViewer = value => ({ ...value, viewer: { ...(value.viewer || {}), following: next } });
    setMaterial(current => type === "program"
      ? { ...current, program: replaceViewer(current.program) }
      : { ...current, subject: replaceViewer(current.subject) });
    try { await toggleFollow(agent, type, entity.id, next); }
    catch (reason) { setError(reason.message); await load(); }
  };
  const toggleOffline = async () => {
    setOfflineWorking(true);
    try {
      if (offline) await removeMaterialOffline(material);
      else await saveMaterialOffline(material);
      setOffline(!offline);
    } catch (reason) { setError(reason.message); }
    finally { setOfflineWorking(false); }
  };
  const publishVersion = async event => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setVersionStatus("Preparando la nueva versión…");
    setError("");
    try {
      const result = await publishMaterialVersion(agent, file, material, setVersionStatus);
      navigate(`/material/${encodeURIComponent(result.materialId)}`);
    } catch (reason) {
      setError(reason.message);
      setVersionStatus("");
    }
  };
  const ownLatest = material.author.did === profile.did && material.isLatestVersion !== false;
  return <main className="page material-page">
    <button className="back-button" onClick={() => navigate(-1)}><ArrowLeft />Volver</button>
    {error && <ErrorNotice message={error} retry={() => setError("")} />}
    <section className="material-detail">
      <div className="document-hero"><span className="document-hero__fold" /><FileText /><span>PDF</span></div>
      <div className="material-detail__copy"><span className="eyebrow">{labelForType(material.documentType)} · {formatBytes(material.sizeBytes)}</span><h1>{material.title}</h1><p>{material.description || "Sin descripción."}</p><div className="tag-row">{material.tags.map(tag => <Link className="tag-link" key={tag} to={`/etiqueta/${encodeURIComponent(tag)}`}>#{tag}</Link>)}</div><button className="author-link large" onClick={() => navigate(`/perfil/${encodeURIComponent(material.author.did)}`)}><Avatar profile={material.author} /><span><strong>{material.author.displayName}</strong><small>@{material.author.handle}</small></span></button></div>
      <aside className="material-detail__meta">
        {material.subject && <MaterialEntityRow icon={BookOpen} label="Asignatura" entity={material.subject} onFollow={() => followEntity("subject", material.subject)} />}
        <MaterialEntityRow icon={GraduationCap} label="Curso o titulación" entity={material.program} onFollow={() => followEntity("program", material.program)} />
        {material.institution && <span><Building2 /><small>Centro</small><strong>{material.institution.name}</strong></span>}
      </aside>
    </section>
    <section className="rating-sheet rating-sheet--prominent"><div className="rating-intro"><span className="eyebrow">Tu opinión cuenta</span><h2>¿Te ha servido?</h2><p>Toca las estrellas para valorar este material.</p></div><div><span className="eyebrow">Valoración de la comunidad</span><Stars value={material.rating.average || 0} count={material.rating.count} size={21} /></div><div><span className="eyebrow">Tu valoración</span><RatingPicker value={rating} onChange={rate} disabled={savingRating} /></div></section>
    <div className="material-actions"><a className="primary-button" href={material.fileURL} target="_blank" rel="noreferrer"><BookOpen />Abrir PDF</a><a className="secondary-button" href={downloadURL} download={material.originalFilename || "documento.pdf"}><Download />Descargar PDF</a><button className={material.viewer.saved ? "secondary-button active" : "secondary-button"} onClick={async () => { await toggleSave(agent, material, !material.viewer.saved); load(); }}><Bookmark fill={material.viewer.saved ? "currentColor" : "none"} />{material.viewer.saved ? "Guardado" : "Guardar"}</button><button className={offline ? "secondary-button active" : "secondary-button"} disabled={offlineWorking} onClick={toggleOffline}><WifiOff />{offline ? "Disponible offline" : "Guardar offline"}</button></div>
    {(versions.length > 1 || ownLatest) && <section className="version-sheet">
      <div><RefreshCw /><span><strong>{versions.length > 1 ? `${versions.length} versiones` : "Primera versión"}</strong><small>Consulta cambios anteriores sin perder el original.</small></span></div>
      <div className="version-links">{versions.map(item => <Link key={item.id} className={item.id === material.id ? "active" : ""} to={`/material/${encodeURIComponent(item.id)}`}>v{item.versionNumber || 1}</Link>)}</div>
      {ownLatest && <><input ref={versionInput} className="visually-hidden" type="file" accept="application/pdf,.pdf" onChange={publishVersion} /><button className="secondary-button compact" disabled={Boolean(versionStatus)} onClick={() => versionInput.current?.click()}><Upload />{versionStatus || "Subir versión corregida"}</button></>}
    </section>}
    <section className="rights-sheet"><ShieldCheck /><div><strong>Procedencia declarada</strong><p>{rightsLabel(material.rightsBasis)}{material.licenseName ? ` · ${material.licenseName}` : ""}</p></div></section>
    <section className="pdf-viewer">
      <header><div><span className="eyebrow">Vista previa</span><strong>{material.originalFilename || `${material.title}.pdf`}</strong></div><a href={material.fileURL} target="_blank" rel="noreferrer">Abrir aparte <ChevronRight /></a></header>
      <Suspense fallback={<div className="pdf-viewer__state"><Loading label="Preparando el visor…" /></div>}>
        <PDFViewer url={material.fileURL} title={material.title} material={material} />
      </Suspense>
    </section>
  </main>;
}

function SettingsPage() {
  const { profile, agent, logout } = useAuth();
  const [exporting, setExporting] = useState(false);
  const [destination, setDestination] = useState("");
  const [migrationStatus, setMigrationStatus] = useState(null);
  const downloadExport = async () => {
    setExporting(true);
    try {
      const blob = await exportRepository(agent);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `pliego-${profile.handle}-${new Date().toISOString().slice(0, 10)}.zip`;
      anchor.click();
      setTimeout(() => URL.revokeObjectURL(url), 1_000);
    } catch (reason) { setMigrationStatus({ error: reason.message }); }
    finally { setExporting(false); }
  };
  const inspectDestination = async event => {
    event.preventDefault();
    setMigrationStatus({ loading: true });
    try {
      const url = new URL(destination.trim());
      if (url.protocol !== "https:") throw new Error("El servidor debe usar una dirección HTTPS.");
      const response = await fetch(new URL("/xrpc/com.atproto.server.describeServer", url));
      if (!response.ok) throw new Error("No parece ser un servidor AT Protocol compatible.");
      const info = await response.json();
      setMigrationStatus({ ok: true, host: url.host, inviteRequired: Boolean(info.inviteCodeRequired) });
    } catch (reason) { setMigrationStatus({ error: reason.message || "No se ha podido comprobar ese servidor." }); }
  };
  return <main className="page settings-page">
    <PageHeader eyebrow="Tu cuenta federada" title="ajustes"><p>Tus datos pertenecen a tu identidad, no a Pliego.</p></PageHeader>
    <div className="settings-grid">
      <section className="settings-card"><Archive /><span><span className="eyebrow">Copia completa</span><h2>Exporta tu cuenta</h2><p>Descarga un ZIP con tu repositorio AT Protocol, todos sus blobs y un manifiesto. Incluye tus perfiles, materiales y archivos federados.</p><button className="primary-button" disabled={exporting} onClick={downloadExport}>{exporting ? "Recogiendo registros y archivos…" : "Descargar mi archivo"}<Download /></button></span></section>
      <section className="settings-card"><Server /><span><span className="eyebrow">Cambiar de proveedor</span><h2>Mudarte a otro PDS</h2><p>Comprueba primero el servidor de destino. Pliego conserva tu DID y volverá a indexar tus datos cuando la migración oficial haya terminado.</p><form onSubmit={inspectDestination}><Field label="Servidor de destino"><input type="url" value={destination} onChange={event => setDestination(event.target.value)} placeholder="https://pds.ejemplo.com" /></Field><button className="secondary-button" disabled={!destination.trim() || migrationStatus?.loading}>{migrationStatus?.loading ? "Comprobando…" : "Comprobar servidor"}<ChevronRight /></button></form>{migrationStatus?.ok && <div className="migration-result success"><Check /><span><strong>{migrationStatus.host} es compatible</strong><small>{migrationStatus.inviteRequired ? "Necesitarás una invitación del proveedor." : "El servidor admite cuentas sin invitación."}</small></span></div>}{migrationStatus?.error && <div className="migration-result error"><span>{migrationStatus.error}</span></div>}<p className="fine-print">El traslado automático requiere credenciales o invitación del nuevo proveedor y confirmar el cambio de identidad. Por seguridad, Pliego no ejecuta esos pasos destructivos todavía.</p><a className="text-button migration-guide" href="https://atproto.com/guides/account-migration" target="_blank" rel="noreferrer">Ver la guía oficial de migración</a></span></section>
    </div>
    <button className="secondary-button settings-logout" onClick={logout}><LogOut />Cerrar sesión en este navegador</button>
  </main>;
}

function TagPage() {
  const { tag: routeTag } = useParams();
  const { profile, agent } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [updating, setUpdating] = useState(false);
  const load = () => query("/v1/tags", { tag: routeTag, viewerDid: profile.did })
    .then(value => { setData(value); setError(""); })
    .catch(reason => setError(reason.message));
  useEffect(() => { load(); }, [routeTag, profile.did]);
  if (!data && !error) return <main className="page"><Loading label={`Buscando #${routeTag}…`} /></main>;
  if (!data) return <main className="page"><ErrorNotice message={error} retry={load} /></main>;
  const changeFollow = async () => {
    const previous = data.tag.viewer.following;
    const next = !previous;
    setData(current => ({ ...current, tag: { ...current.tag, viewer: { following: next } } }));
    setUpdating(true);
    try { await toggleFollow(agent, "tag", data.tag.name, next); await load(); }
    catch (reason) {
      setData(current => ({ ...current, tag: { ...current.tag, viewer: { following: previous } } }));
      setError(reason.message);
    } finally { setUpdating(false); }
  };
  return <main className="page tag-page">
    <PageHeader eyebrow="Muro por etiqueta" title={`#${data.tag.name}`} action={<button className={data.tag.viewer.following ? "secondary-button compact active" : "primary-button compact"} disabled={updating} onClick={changeFollow}>{data.tag.viewer.following ? <Check /> : <Plus />}{data.tag.viewer.following ? "Siguiendo" : "Seguir etiqueta"}</button>}>
      <p>{data.tag.counts.materials} materiales · {data.tag.counts.followers} seguidores</p>
    </PageHeader>
    {error && <ErrorNotice message={error} retry={() => setError("")} />}
    {data.materials.length ? <div className="material-list">{data.materials.map(material => <MaterialCard key={material.id} material={material} onToggleSave={(item, saved) => toggleSave(agent, item, saved)} />)}</div> : <EmptyState icon={Hash} title="Todavía no hay materiales" body="Cuando alguien publique con esta etiqueta, aparecerá aquí y también en tu inicio si la sigues." />}
  </main>;
}

function NotificationsOverlay({ onClose }) {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  useEffect(() => { query("/v1/notifications", { viewerDid: profile.did }).then(data => setItems(data.notifications)); }, [profile.did]);
  return <Overlay title="Notificaciones" onClose={onClose}>{!items ? <Loading /> : items.length ? <div className="notification-list">{items.map(item => <button key={item.id} onClick={() => { onClose(); navigate(item.material ? `/material/${item.material.id}` : `/perfil/${encodeURIComponent(item.actor.did)}`); }}><Avatar profile={item.actor} /><span><strong>{notificationCopy(item)}</strong><small>{formatDate(item.createdAt)}</small></span><ChevronRight /></button>)}</div> : <EmptyState icon={Bell} title="Todo al día" body="Aquí aparecerán nuevos seguidores, valoraciones y materiales de tu red." />}</Overlay>;
}

function RelationshipsOverlay({ profile, kind, viewerDid, onClose }) {
  const navigate = useNavigate();
  const [people, setPeople] = useState(null);
  useEffect(() => { query(`/v1/profiles/${encodeURIComponent(profile.did)}/${kind}`, { viewerDid }).then(data => setPeople(data.people)); }, [profile.did, kind, viewerDid]);
  return <Overlay title={kind === "followers" ? "Seguidores" : "Siguiendo"} onClose={onClose}>{!people ? <Loading /> : people.length ? <div className="relationship-list">{people.map(person => <button key={person.did} onClick={() => { onClose(); navigate(`/perfil/${encodeURIComponent(person.did)}`); }}><Avatar profile={person} /><span><strong>{person.displayName}</strong><small>@{person.handle}</small></span><ChevronRight /></button>)}</div> : <EmptyState icon={Users} title="Todavía no hay nadie" body="Las conexiones aparecerán aquí." />}</Overlay>;
}

function LegalPage({ kind }) {
  const privacy = kind === "privacy";
  return <main className="legal-page"><Link to="/"><PliegoLogo compact /></Link><article><span className="eyebrow">Pliego</span><h1>{privacy ? "Privacidad" : "Condiciones de uso"}</h1>{privacy ? <><p>Pliego usa AT Protocol. La identidad, las sesiones y los archivos permanecen en el PDS elegido por cada persona. Nuestro AppView conserva índices públicos necesarios para búsqueda, feed y estadísticas, además de las denuncias necesarias para proteger a la comunidad.</p><p>No recibimos ni almacenamos tu contraseña. Tu proveedor PDS gestiona el acceso y puede permitirte revocar cada sesión. Puedes eliminar tu perfil y todo el contenido de Pliego desde Ajustes, sin borrar tu cuenta externa de Bluesky.</p></> : <><p>Sólo puedes publicar materiales propios, de dominio público, con licencia abierta o para los que tengas permiso expreso. No publiques libros comerciales, datos personales ajenos, amenazas, acoso, odio, contenido sexual explícito ni material peligroso.</p><p>Desde la app puedes denunciar materiales y perfiles, bloquear cuentas y ocultar de inmediato su contenido. Pliego revisará los avisos y puede retirar del índice aquello que infrinja estas normas o ponga a alguien en riesgo.</p></>}<p>¿Necesitas ayuda? Usa el <Link to="/contacto">formulario de contacto</Link>.</p><Link className="secondary-button compact" to="/">Volver a Pliego</Link></article></main>;
}

function ContactPage() {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState("");
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  const submit = async event => {
    event.preventDefault();
    setState("sending");
    setError("");
    try {
      await api("/v1/support/requests", { method: "POST", body: { email, message, website } });
      setState("sent");
    } catch (caught) {
      setError(caught.message);
      setState("idle");
    }
  };
  return <main className="legal-page"><Link to="/"><PliegoLogo compact /></Link><article><span className="eyebrow">Soporte</span><h1>¿En qué te ayudamos?</h1>{state === "sent" ? <><p>Hemos recibido tu mensaje. Te responderemos en el correo que has indicado.</p><Link className="secondary-button compact" to="/">Volver a Pliego</Link></> : <form onSubmit={submit}><Field label="Tu correo"><input type="email" required value={email} onChange={event => setEmail(event.target.value)} autoComplete="email" /></Field><Field label="Mensaje"><textarea required minLength="10" maxLength="4000" value={message} onChange={event => setMessage(event.target.value)} rows="7" /></Field><input aria-hidden="true" tabIndex="-1" autoComplete="off" value={website} onChange={event => setWebsite(event.target.value)} style={{ position: "absolute", left: "-9999px" }} />{error && <p className="form-error">{error}</p>}<button className="primary-button" disabled={state === "sending"}>{state === "sending" ? "Enviando…" : "Enviar mensaje"}</button></form>}</article></main>;
}

function Field({ label, hint, children }) { return <label className="field"><span>{label}{hint && <small>{hint}</small>}</span>{children}</label>; }
function SectionHeader({ title, detail }) { return <div className="section-header"><h2>{title}</h2>{detail && <span>{detail}</span>}</div>; }
function EntityCard({ icon: Icon, title, subtitle, following, onFollow, onOpen }) { return <article className={onOpen ? "entity-card entity-card--open" : "entity-card"} onClick={onOpen}><span className="entity-card__icon"><Icon /></span><div><strong>{title}</strong><small>{subtitle}</small></div>{onFollow && <button className={following ? "follow-button active" : "follow-button"} aria-label={following ? `Dejar de seguir ${title}` : `Seguir ${title}`} onClick={event => { event.stopPropagation(); onFollow(); }}>{following ? <Check /> : "+"}</button>}</article>; }
function MaterialEntityRow({ icon: Icon, label, entity, onFollow }) { return <span><Icon /><small>{label}</small><strong>{entity.name}</strong><button className={entity.viewer?.following ? "follow-button active" : "follow-button"} aria-label={entity.viewer?.following ? `Dejar de seguir ${entity.name}` : `Seguir ${entity.name}`} onClick={onFollow}>{entity.viewer?.following ? <Check /> : "+"}</button></span>; }
function ErrorNotice({ message, retry }) { return <div className="error-notice"><strong>No ha salido bien</strong><span>{message}</span>{retry && <button onClick={retry}>Reintentar</button>}</div>; }
function rightsLabel(value) { return ({ own: "Material propio", permission: "Compartido con permiso", open_license: "Licencia abierta", public_domain: "Dominio público" })[value] || "Sin especificar"; }
function notificationCopy(item) { if (item.kind === "new_follower") return `${item.actor?.displayName || "Alguien"} ha empezado a seguirte`; if (item.kind === "material_rating") return `${item.actor?.displayName || "Alguien"} ha valorado “${item.material?.title}”`; if ((item.material?.versionNumber || 1) > 1) return `${item.actor?.displayName || "Alguien"} ha actualizado “${item.material?.title}”`; return `${item.actor?.displayName || "Alguien"} ha publicado “${item.material?.title}”`; }
function formatDate(value) { return new Intl.DateTimeFormat("es", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }).format(new Date(value)); }

function PreviewApp() {
  const sampleProfile = { did: "did:plc:preview", displayName: "Marina López", handle: "marina.estudia", avatarURL: null };
  const baseMaterial = {
    id: "preview", uri: "at://did:plc:preview/app.pliego.feed.material/example", cid: "preview",
    title: "La célula: estructura y funciones", description: "Resumen visual del tema 3 con esquemas, mitosis y ejercicios de repaso.",
    documentType: "resumen", sizeBytes: 2_600_000, pageCount: 18, tags: ["biología", "selectividad", "tema 3"],
    author: sampleProfile, program: { name: "Bachillerato de Ciencias y Tecnología" }, subject: { name: "Biología" },
    rating: { average: 4.5, count: 27 }, viewer: { saved: false }
  };
  const materials = [
    baseMaterial,
    { ...baseMaterial, id: "preview-2", title: "Derivadas y representación de funciones", documentType: "ejercicios", description: "Problemas resueltos paso a paso.", subject: { name: "Matemáticas II" }, pageCount: 32, rating: { average: 4.8, count: 41 }, tags: ["matemáticas", "ejercicios"] },
    { ...baseMaterial, id: "preview-3", title: "La Generación del 27", documentType: "apuntes", description: "Autores, etapas y recursos para comentario de texto.", subject: { name: "Lengua y Literatura" }, pageCount: 14, rating: { average: 4.7, count: 18 }, tags: ["literatura", "selectividad"] },
    { ...baseMaterial, id: "preview-4", title: "Química orgánica esencial", documentType: "resumen", description: "Reacciones, mecanismos y trucos visuales.", subject: { name: "Química" }, pageCount: 25, rating: { average: 4.6, count: 33 }, tags: ["química", "orgánica"] }
  ];
  const navigation = [
    ["feed", "Inicio", Home], ["explore", "Explorar", Search], ["upload", "Subir", Upload],
    ["saved", "Guardados", Bookmark], ["profile", "Perfil", User]
  ];
  const [section, setSection] = useState("feed");
  const [savedIDs, setSavedIDs] = useState(new Set(["preview-3"]));
  const [following, setFollowing] = useState(new Set(["Biología"]));
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [previewRating, setPreviewRating] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [toast, setToast] = useState("");
  const changeSection = next => { setSelectedMaterial(null); setSection(next); window.scrollTo(0, 0); };
  const toggleDemoSave = material => {
    setSavedIDs(current => {
      const next = new Set(current);
      next.has(material.id) ? next.delete(material.id) : next.add(material.id);
      return next;
    });
  };
  const materialCards = list => <div className="material-list">{list.map(item => <MaterialCard
    key={item.id}
    material={{ ...item, viewer: { saved: savedIDs.has(item.id) } }}
    onOpen={setSelectedMaterial}
    onToggleSave={toggleDemoSave}
    onOpenAuthor={() => changeSection("profile")}
    onOpenTag={tag => { setToast(`Explorando #${tag}`); changeSection("explore"); }}
    onDownload={() => setToast("Descarga preparada en la app real")}
  />)}</div>;
  const nav = navigation.map(([key, label, Icon]) => <button key={key} type="button" className={section === key && !selectedMaterial ? "nav-item active" : "nav-item"} onClick={() => changeSection(key)}><Icon /><span>{label}</span></button>);

  let content;
  if (selectedMaterial) {
    content = <main className="page material-page preview-detail">
      <button className="secondary-button compact" type="button" onClick={() => setSelectedMaterial(null)}><ArrowLeft />Volver</button>
      <div className="material-detail__top">
        <div className="document-hero"><FileText /><span className="document-hero__fold" /></div>
        <section className="material-detail__copy">
          <span className="eyebrow">{labelForType(selectedMaterial.documentType)}</span>
          <h1>{selectedMaterial.title}</h1>
          <p>{selectedMaterial.description}</p>
          <div className="tag-row">{selectedMaterial.tags.map(tag => <span key={tag}>#{tag}</span>)}</div>
          <button className="primary-button" type="button" onClick={() => toggleDemoSave(selectedMaterial)}><Bookmark fill={savedIDs.has(selectedMaterial.id) ? "currentColor" : "none"} />{savedIDs.has(selectedMaterial.id) ? "Guardado" : "Guardar en biblioteca"}</button>
        </section>
      </div>
      <section className="rating-sheet rating-sheet--prominent">
        <div className="rating-intro"><span className="eyebrow">Tu opinión cuenta</span><h2>¿Te ha servido?</h2><p>Toca las estrellas para valorar este material.</p></div>
        <div><span className="eyebrow">Valoración de la comunidad</span><Stars value={selectedMaterial.rating.average || 0} count={selectedMaterial.rating.count} size={21} /></div>
        <div><span className="eyebrow">Tu valoración</span><RatingPicker value={previewRating} onChange={value => { setPreviewRating(value); setToast(`Has valorado con ${String(value).replace(".", ",")} estrellas`); }} /></div>
      </section>
      <section className="pdf-viewer preview-document"><header><strong>{selectedMaterial.pageCount} páginas · Vista del documento</strong></header><div><h2>{selectedMaterial.title}</h2><p>Apuntes, esquemas y ejemplos preparados para estudiar.</p></div></section>
    </main>;
  } else if (section === "explore") {
    content = <main className="page explore-page"><PageHeader eyebrow="Comunidad" title="encuentra tu próxima idea"><p>Busca por asignatura, curso, etiqueta o persona.</p></PageHeader>
      <div className="search-box"><Search /><input aria-label="Buscar" placeholder="Prueba «biología», «selectividad»…" /></div>
      <SectionHeader title="Asignaturas que te pueden interesar" />
      <div className="entity-grid">{["Biología", "Matemáticas II", "Historia del Arte"].map((title, index) => <EntityCard key={title} icon={index === 2 ? Building2 : BookOpen} title={title} subtitle={index === 1 ? "124 materiales" : "Comunidad activa"} following={following.has(title)} onFollow={() => setFollowing(current => { const next = new Set(current); next.has(title) ? next.delete(title) : next.add(title); return next; })} />)}</div>
      <SectionHeader title="Materiales recientes" />{materialCards(materials.slice(1))}
    </main>;
  } else if (section === "upload") {
    content = <main className="page upload-page"><PageHeader eyebrow="Comparte lo que sabes" title="sube algo útil"><p>Convierte tus apuntes en un material que otra persona pueda encontrar.</p></PageHeader>
      <div className="upload-layout"><label className="drop-zone"><Upload className="drop-zone__icon" /><strong>Arrastra tu PDF o elígelo aquí</strong><small>PDF · máximo 50 MB</small><input className="visually-hidden" type="file" accept="application/pdf" onChange={event => event.target.files?.[0] && setToast(`${event.target.files[0].name} preparado`)} /></label>
      <section className="form-sheet"><Field label="Título"><input defaultValue="Mis apuntes de clase" /></Field><Field label="Descripción"><textarea defaultValue="Resumen claro, esquemas y ejercicios." /></Field><Field label="Asignatura"><select defaultValue="Biología"><option>Biología</option><option>Matemáticas II</option><option>Historia del Arte</option></select></Field><button className="primary-button" type="button" onClick={() => setToast("Material listo para publicar")}><Sparkles />Preparar publicación</button></section></div>
    </main>;
  } else if (section === "saved") {
    const saved = materials.filter(item => savedIDs.has(item.id));
    content = <main className="page saved-page"><PageHeader eyebrow="Tu espacio" title="tu biblioteca"><p>Apuntes guardados y lecturas en curso.</p></PageHeader>{saved.length ? materialCards(saved) : <EmptyState icon={Bookmark} title="Tu biblioteca está vacía" body="Guarda cualquier tarjeta para verla aquí." action={<button className="primary-button compact" onClick={() => changeSection("feed")}>Ir al inicio</button>} />}</main>;
  } else if (section === "profile") {
    content = <main className="page profile-page"><PageHeader eyebrow="Tu identidad" title="tu mesa, a tu manera" /><section className="profile-hero"><div className="profile-hero__identity"><Avatar profile={sampleProfile} size="large" /><div><span className="eyebrow">@{sampleProfile.handle}</span><h1>{sampleProfile.displayName}</h1><p>Biología, arte y apuntes que hacen más fácil estudiar.</p></div></div><div className="profile-hero__study"><GraduationCap /><span><small>Estudiando</small><strong>2º Bachillerato · Ciencias</strong></span></div><div className="profile-stats"><div><strong>4</strong><span>Materiales</span></div><button><strong>128</strong><span>Seguidores</span></button><button><strong>36</strong><span>Siguiendo</span></button></div></section><SectionHeader title="Tus materiales" />{materialCards(materials.slice(0, 2))}</main>;
  } else {
    content = <main className="page feed-page"><PageHeader eyebrow="Tu red educativa" title="lo nuevo en tu mesa"><p>Apuntes de personas, asignaturas y cursos que sigues.</p></PageHeader>{materialCards(materials)}</main>;
  }

  return <div className="app-shell preview-shell">
    <aside className="sidebar"><PliegoLogo /><nav>{nav}</nav><div className="sidebar__account"><Avatar profile={sampleProfile} /><span><strong>{sampleProfile.displayName}</strong><small>@{sampleProfile.handle}</small></span></div></aside>
    <div className="app-main"><div className="utility-bar"><span className="utility-bar__course">2º Bachillerato · Ciencias</span><button className="icon-button notification-button" onClick={() => setShowNotifications(true)} aria-label="Notificaciones"><Bell /></button></div>{content}</div>
    <nav className="mobile-tabs">{nav}</nav>
    {showNotifications && <Overlay title="Notificaciones" onClose={() => setShowNotifications(false)}><div className="notification-list"><button onClick={() => { setShowNotifications(false); setSelectedMaterial(materials[1]); }}><Avatar profile={sampleProfile} /><span><strong>Nuevo material de Matemáticas II</strong><small>Hace 12 minutos</small></span><ChevronRight /></button><button onClick={() => setShowNotifications(false)}><Avatar profile={{ displayName: "Leo" }} /><span><strong>Leo ha empezado a seguirte</strong><small>Ayer</small></span><ChevronRight /></button></div></Overlay>}
    {toast && <button className="preview-toast" type="button" onClick={() => setToast("")}>{toast}<Check /></button>}
  </div>;
}

function PreviewPDF() {
  const params = new URLSearchParams(window.location.search);
  const url = params.get("file") || "https://pliego-api.mydash.workers.dev/v1/materials/c1ac7ac3-b981-422f-a08b-cc97efe5e40c/file";
  const [previewRating, setPreviewRating] = useState(2.5);
  return <main className="page material-page">
    <section className="rating-sheet"><div><span className="eyebrow">Prueba de puntuación</span><RatingPicker value={previewRating} onChange={setPreviewRating} /></div></section>
    <section className="pdf-viewer">
      <header><div><span className="eyebrow">Vista previa</span><strong>Documento de prueba.pdf</strong></div></header>
      <Suspense fallback={<div className="pdf-viewer__state"><Loading label="Preparando el visor…" /></div>}>
        <PDFViewer url={url} title="Documento de prueba" />
      </Suspense>
    </section>
  </main>;
}
